import styled from "styled-components";
import {
  GoogleIcon,
  ICON_ID,
  ICON_SIZES,
} from "../../components/icons/google-icon";
import { useNavigate } from "react-router-dom";
import { useEffect } from "react";
import { useAppDispatch } from "../../hooks/useAppDispatch";
import { setCurrentPage } from "../../store/features/authentication/user-slice";
import { useAppSelector } from "../../hooks/useAppSelector";
import { fetchDepartments, updateDepartment } from "../../store/features/organisation/department-slice";
import { ApplicationDepartment } from "../../models/organisation/application-department";
import Checkbox from "../../components/controls/checkbox";
import WhatsAppSignup from "../../components/whats-app-signup";
import { ApplicationRole } from "../../models/authentication/application-roles";


export default function AdminHome() {
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const departments = useAppSelector((state) => state.department.departments)?.departments;
  const role = useAppSelector((state) => state.user.user?.role);
  const isOwner = role === ApplicationRole.Owner;
  const canManageDepartments = role === ApplicationRole.Owner || role === ApplicationRole.SupplierManager;
  useEffect(() => {
    dispatch(setCurrentPage(""))
  }, []);

  useEffect(() => {
    if (isOwner && !departments) {
      dispatch(fetchDepartments());
    }
  }, [departments, isOwner])


  const toggleOrdering = (active: boolean, department: ApplicationDepartment) => {
    dispatch(updateDepartment({
      ...department,
      active
    }))
  }

  return (
    <Container>
      <Selection onClick={() => navigate("/admin/approvals")}>
        <GoogleIcon
          id={ICON_ID.Approval}
          isNeutral
          size={ICON_SIZES.Large}
        ></GoogleIcon>
        Approvals
        <SelectionDescription>
          Approve order requests from store managers across all branches.
        </SelectionDescription>
      </Selection>
      <Selection onClick={() => navigate("/supplier")}>
        <GoogleIcon
          id={ICON_ID.Groups}
          isNeutral
          size={ICON_SIZES.Large}
        ></GoogleIcon>
        Manage suppliers
        <SelectionDescription>
          Your supplier management portal. Create new suppliers, update existing
          suppliers and update supplier products.
        </SelectionDescription>
      </Selection>
      <Selection onClick={() => navigate("/admin/products")}>
        <GoogleIcon
          id={ICON_ID.ViewList}
          isNeutral
          size={ICON_SIZES.Large}
        ></GoogleIcon>
        Manage Products
        <SelectionDescription>
          View the  product list across all branches. Create, update or re categorise products.
        </SelectionDescription>
      </Selection>

      {canManageDepartments && (
        <Selection onClick={() => navigate("/admin/department")}>
          <GoogleIcon
            id={ICON_ID.Category}
            isNeutral
            size={ICON_SIZES.Large}
          ></GoogleIcon>
          Manage Departments
          <SelectionDescription>
            Create new departments or edit existing ones, manage categories, and
            adjust department settings all in one place
          </SelectionDescription>
        </Selection>
      )}

      {isOwner && (
        <>
          <Selection onClick={() => navigate("/admin/user")}>
            <GoogleIcon
              id={ICON_ID.ManageAccounts}
              isNeutral
              size={ICON_SIZES.Large}
            ></GoogleIcon>
            Manage Users
            <SelectionDescription>
              Create new users, request new passwords and update details of users.
            </SelectionDescription>
          </Selection>
          <Selection onClick={() => navigate("/admin/returns")}>
            <GoogleIcon
              id={ICON_ID.ReceiptLong}
              isNeutral
              size={ICON_SIZES.Large}
            ></GoogleIcon>
            Returns Worklist
            <SelectionDescription>
              View all returns across users and mark each return as resolved.
            </SelectionDescription>
          </Selection>
          <Selection onClick={() => navigate("/admin/overrides")}>
            <GoogleIcon
              id={ICON_ID.Selection}
              isNeutral
              size={ICON_SIZES.Large}
            ></GoogleIcon>
            Product Overrides
            <SelectionDescription>
              View all active product override mappings across suppliers.
            </SelectionDescription>
          </Selection>
          <Selection $color="success" onClick={() => navigate("/admin/communications")}>
            <WhatsAppBadge>
              <WhatsAppMark>WA</WhatsAppMark>
            </WhatsAppBadge>
            Communications
            <SelectionDescription>
              Open the WhatsApp-style inbox for supplier conversations, message history, and replies.
            </SelectionDescription>
          </Selection>
          <Selection>
            Enable Departments
            <SelectionDescription>
              Use the below toggles to enable/disable a department quickly.
              <QuickDepartment>
                {departments?.map((department) => {
                  return <Department>{department.name}
                    <Checkbox
                      label="Active"
                      checked={department.active}
                      onChange={(event) => toggleOrdering(event.target.checked, department)}
                    ></Checkbox>
                  </Department>
                })}
              </QuickDepartment>
            </SelectionDescription>
          </Selection>
          <Selection>
            <GoogleIcon
              id={ICON_ID.ManageAccounts}
              isNeutral
              size={ICON_SIZES.Large}
            ></GoogleIcon>
            Integrations
            <SelectionDescription>
              <WhatsAppSignup />
            </SelectionDescription>
          </Selection>
        </>
      )}
    </Container>
  );
}

const Container = styled.div`
  display: flex;
  flex-wrap:wrap;
  gap: 10px;
  justify-content: center;
`;

const QuickDepartment = styled.div`
text-align:left;
margin-top:10px;
display:flex;
flex-direction:column;
gap:4px;

div {
border-bottom: 1px solid ${(props) => props.theme.application.border};
padding:5px;

}
`

const Department = styled.div`
display:flex;
justify-content: space-between;
align-items: center;

`
const WhatsAppBadge = styled.div`
  width: 72px;
  height: 72px;
  border-radius: 22px;
  display: flex;
  align-items: center;
  justify-content: center;
  margin-bottom: 8px;
  background: linear-gradient(135deg, #075e54 0%, #128c7e 52%, #25d366 100%);
  box-shadow: 0 12px 24px rgba(7, 94, 84, 0.25);
`;

const WhatsAppMark = styled.span`
  color: #ffffff;
  font-size: 1rem;
  font-weight: 800;
  letter-spacing: 0.08em;
`;
interface SelectionContainer {
  $color?: 'success' | 'danger';
}


const Selection = styled.div<SelectionContainer>`
  width:100%;

 @media (min-width: 606px) {
     width:25%;
  }
  display: flex;
  flex-direction: column;
  align-items: center;
  text-align: center;
  padding: 20px;
  border-radius: 4px;
  background: ${(props) => props.theme.application.background};
  border: 1px solid ${(props) => props.theme.application.border};
  box-shadow: 0 4px 8px rgba(0, 0, 0, 0.1);
  cursor: pointer;
  transition: all 0.3s ease;
  ${(props) =>
    props.$color &&
    `
      background: ${props.theme.application.scheme[props.$color]};
      color: white;

       span, p {
       color: white;
  }
    `}

  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 6px 12px rgba(0, 0, 0, 0.15);
  }

`;


const SelectionDescription = styled.p`
  font-size: 1rem;
  font-weight: 400;
  color: ${(props) => props.theme.application.text};
  padding: 0 15px;
`;

